import { useId } from "react";
import { capabilityLevels } from "@zylarity/contracts";
import "./capability-map.css";

export type CapabilityMapAxis = {
  label: string;
  shortLabel: string;
  candidate?: number | null | undefined;
  requirement?: number | null | undefined;
};

function recordedLevel(value: number | null | undefined) {
  return typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= 5
    ? value
    : null;
}

function describeLevel(value: number | null) {
  return value === null ? "Unassessed" : `${capabilityLevels[value - 1]} (${value}/5)`;
}

/** The same chart renders public examples, candidate profiles, jobs and their overlay. */
export function CapabilityMap({
  axes: suppliedAxes,
  candidate = false,
  job = false,
  responsibilityLabels = false,
}: {
  axes: readonly CapabilityMapAxis[];
  candidate?: boolean;
  job?: boolean;
  /** Public examples describe responsibility scope, not assessed competence. */
  responsibilityLabels?: boolean;
}) {
  const id = useId();
  const axes = suppliedAxes.map((axis) => ({
    ...axis,
    candidate: recordedLevel(axis.candidate),
    requirement: recordedLevel(axis.requirement),
  }));
  if (axes.length === 0) {
    return (
      <figure className="zy-capability-map">
        <p>No work domains are available for this map.</p>
      </figure>
    );
  }
  const point = (index: number, value: number) => {
    const angle = -Math.PI / 2 + (index * Math.PI * 2) / axes.length;
    return { x: 300 + Math.cos(angle) * value, y: 245 + Math.sin(angle) * value };
  };
  const radius = (value: number | null | undefined) => (value ?? 0) * 30;
  const polygon = (values: readonly (number | null | undefined)[]) =>
    values
      .map((value, index) => {
        const p = point(index, radius(value));
        return `${p.x},${p.y}`;
      })
      .join(" ");
  const lines = (label: string) =>
    label.split(" ").reduce<string[]>((result, word) => {
      const last = result.at(-1);
      if (last && `${last} ${word}`.length <= 15) result[result.length - 1] = `${last} ${word}`;
      else result.push(word);
      return result;
    }, []);
  const hasCandidate = candidate && axes.some((axis) => axis.candidate != null);
  const hasJob = job && axes.some((axis) => axis.requirement != null);
  const completeCandidate = hasCandidate && axes.every((axis) => axis.candidate != null);
  const completeJob = hasJob && axes.every((axis) => axis.requirement != null);
  const labels = axes.map((axis, index) => {
    const label = point(index, 190);
    const parts = lines(axis.shortLabel);
    return (
      <text
        key={axis.label}
        x={label.x}
        y={label.y - (parts.length - 1) * 9}
        textAnchor={Math.abs(label.x - 300) < 1 ? "middle" : label.x > 300 ? "start" : "end"}
        dominantBaseline="middle"
        className="zy-capability-map__label"
      >
        {parts.map((part, i) => (
          <tspan key={i} x={label.x} dy={i === 0 ? 0 : 18}>
            {part}
          </tspan>
        ))}
      </text>
    );
  });
  const scale = [1, 2, 3, 4, 5].map((value) => (
    <text key={value} x="309" y={249 - value * 30} className="zy-capability-map__scale">
      {value}
    </text>
  ));
  return (
    <figure
      className={`zy-capability-map${responsibilityLabels ? " zy-capability-map--responsibility" : ""}`}
    >
      <svg
        viewBox="0 0 600 485"
        role="img"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-desc`}
      >
        <title id={`${id}-title`}>
          {candidate && job
            ? "Candidate and job capability overlay"
            : job
              ? "Job capability map"
              : "Candidate capability map"}
        </title>
        <desc id={`${id}-desc`}>
          {axes
            .map(
              (axis) =>
                `${axis.label}: ${candidate ? `candidate ${axis.candidate ?? "unknown"}; ` : ""}${job ? `job ${axis.requirement ?? "unknown"}; ` : ""}`,
            )
            .join(" ")}{" "}
          {responsibilityLabels
            ? "Five responsibility levels. Unknown means the responsibility scope has not been recorded."
            : "Five levels. Unknown is not a zero skill rating."}
        </desc>
        <defs>
          <linearGradient id={`${id}-fill`} x1="0%" y1="0%" x2="90%" y2="100%">
            <stop
              offset="0%"
              stopColor="var(--map-candidate)"
              stopOpacity={responsibilityLabels ? "0.22" : "0.35"}
            />
            <stop
              offset="100%"
              stopColor="var(--map-candidate)"
              stopOpacity={responsibilityLabels ? "0.06" : "0.04"}
            />
          </linearGradient>
          {responsibilityLabels ? (
            <linearGradient id={`${id}-job-fill`} x1="0%" y1="0%" x2="90%" y2="100%">
              <stop offset="0%" stopColor="var(--map-job)" stopOpacity="0.12" />
              <stop offset="100%" stopColor="var(--map-job)" stopOpacity="0.035" />
            </linearGradient>
          ) : null}
        </defs>
        {responsibilityLabels && completeJob ? (
          <polygon
            data-series-fill="job"
            points={polygon(axes.map((axis) => axis.requirement))}
            fill={`url(#${id}-job-fill)`}
            className="zy-capability-map__series-fill"
          />
        ) : null}
        {responsibilityLabels && completeCandidate ? (
          <polygon
            data-series-fill="candidate"
            points={polygon(axes.map((axis) => axis.candidate))}
            fill={`url(#${id}-fill)`}
            className="zy-capability-map__series-fill"
          />
        ) : null}
        {[1, 2, 3, 4, 5].map((value) => (
          <polygon
            key={value}
            points={polygon(axes.map(() => value))}
            className="zy-capability-map__grid"
          />
        ))}
        {axes.map((axis, index) => {
          const end = point(index, 150);
          return (
            <g key={axis.label}>
              <line x1="300" y1="245" x2={end.x} y2={end.y} className="zy-capability-map__axis" />
              {responsibilityLabels ? null : labels[index]}
            </g>
          );
        })}
        {responsibilityLabels ? null : scale}
        {completeCandidate ? (
          <polygon
            data-series="candidate"
            points={polygon(axes.map((a) => a.candidate))}
            fill={responsibilityLabels ? "none" : `url(#${id}-fill)`}
            className="zy-capability-map__candidate"
          />
        ) : null}
        {completeJob ? (
          <polygon
            data-series="job"
            points={polygon(axes.map((a) => a.requirement))}
            className="zy-capability-map__job"
          />
        ) : null}
        {axes.map((axis, index) => {
          const c = point(index, radius(axis.candidate));
          const j = point(index, radius(axis.requirement));
          const comparable = candidate && job && axis.candidate != null && axis.requirement != null;
          return (
            <g key={axis.label}>
              {comparable && axis.candidate !== axis.requirement ? (
                <line
                  x1={c.x}
                  y1={c.y}
                  x2={j.x}
                  y2={j.y}
                  className={
                    axis.candidate! < axis.requirement!
                      ? "zy-capability-map__gap"
                      : "zy-capability-map__strength"
                  }
                />
              ) : null}
              {candidate && axis.candidate != null ? (
                <circle cx={c.x} cy={c.y} r="5" className="zy-capability-map__candidate-point">
                  <title>{`${axis.label}: candidate ${axis.candidate}/5`}</title>
                </circle>
              ) : null}
              {job && axis.requirement != null ? (
                <path
                  d={`M ${j.x} ${j.y - 8} l 8 8 -8 8 -8 -8 Z`}
                  className="zy-capability-map__job-point"
                >
                  <title>{`${axis.label}: job ${axis.requirement}/5`}</title>
                </path>
              ) : null}
            </g>
          );
        })}
        {responsibilityLabels ? (
          <g className="zy-capability-map__annotations">
            {labels}
            {scale}
          </g>
        ) : null}
      </svg>
      <figcaption className="zy-capability-map__legend">
        {candidate ? (
          <span>
            <i className="zy-capability-map__key-candidate" />
            Candidate
          </span>
        ) : null}
        {job ? (
          <span>
            <i className="zy-capability-map__key-job" />
            Job requirement
          </span>
        ) : null}
        {candidate && job ? (
          <>
            <span>
              <i className="zy-capability-map__key-gap" />
              {responsibilityLabels ? "Needs follow-up" : "Potential gap"}
            </span>
            <span>
              <i className="zy-capability-map__key-strength" />
              {responsibilityLabels ? "Relative strengths" : "Potential strength"}
            </span>
          </>
        ) : null}
      </figcaption>
      <p>
        Unassessed:{" "}
        {axes
          .filter(
            (axis) => (candidate && axis.candidate == null) || (job && axis.requirement == null),
          )
          .map((axis) => axis.shortLabel)
          .join(", ") || "None"}
        . Missing evidence is not zero ability.
      </p>
      {candidate || job ? (
        <details className="zy-capability-map__help zy-capability-map__values">
          <summary>View responsibility levels by work area</summary>
          <dl className="zy-capability-map__values-list">
            {axes.map((axis) => (
              <div key={axis.label}>
                <dt>{axis.label}</dt>
                <dd>
                  {candidate ? <span>Candidate: {describeLevel(axis.candidate)}</span> : null}
                  {job ? <span>Job requirement: {describeLevel(axis.requirement)}</span> : null}
                  {candidate && job && axis.candidate !== null && axis.requirement !== null ? (
                    <span>
                      Comparison:{" "}
                      {axis.candidate === axis.requirement
                        ? "Same responsibility level"
                        : axis.candidate < axis.requirement
                          ? responsibilityLabels
                            ? "Needs follow-up"
                            : "Potential gap"
                          : responsibilityLabels
                            ? "Relative strength"
                            : "Potential strength"}
                    </span>
                  ) : null}
                </dd>
              </div>
            ))}
          </dl>
        </details>
      ) : null}
      <details className="zy-capability-map__help">
        <summary>How to read the five levels and comparison</summary>
        <p>
          Each spoke is a work domain. Distance from the center describes the scope of
          responsibility recorded for that domain, not years of experience or a test score.
        </p>
        <ol>
          <li>
            <strong>Awareness:</strong> Recognizes the concepts with explicit supporting evidence. A
            topic mention alone is unassessed.
          </li>
          <li>
            <strong>Assisted:</strong> Performs tasks with guidance or supervision.
          </li>
          <li>
            <strong>Independent:</strong> Performs the work independently.
          </li>
          <li>
            <strong>Leads delivery:</strong> Leads a team or owns delivery of that work.
          </li>
          <li>
            <strong>Defines strategy:</strong> Sets organization-wide direction, standards or
            strategy.
          </li>
        </ol>
        {responsibilityLabels ? (
          <p>
            {candidate && job
              ? "Candidate experience uses a solid outline and round markers. Role requirements use a dashed outline and diamond markers. The shaded areas show each recorded responsibility profile. A dotted segment marks a relative strength where the candidate describes broader responsibility than the role requires; a solid segment marks a role expectation that needs follow-up. These comparisons do not establish competence or suitability for the role."
              : job
                ? "The dashed outline, diamond markers and shaded area show the responsibility expected in each work area. They do not describe a candidate’s tested ability."
                : "The solid outline, round markers and shaded area show the responsibility described in the candidate’s experience. They do not establish tested ability or suitability for a role."}
          </p>
        ) : (
          <p>
            Candidate experience uses a solid outline and round markers. Job requirements use a
            dashed outline and diamond markers. A dotted segment marks a potential strength where
            the candidate describes broader responsibility than the job. A solid segment marks a
            potential gap where the job asks for broader responsibility. For example, candidate 4
            and job 3 shows a potential strength of one level.
          </p>
        )}
        <p>
          Unknown or unassessed domains have no plotted point. Incomplete series have no filled
          polygon. Confirm text-derived suggestions through discussion or assessment; a higher level
          does not automatically mean a better match.
        </p>
      </details>
      {candidate && !hasCandidate ? <p>Candidate domain levels are unassessed.</p> : null}
      {job && !hasJob ? (
        <p>
          Job domain requirements are not recorded yet. Add the job description to build the
          comparison.
        </p>
      ) : null}
    </figure>
  );
}
